import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import type { StepProps } from "./StepComponents";
import {
  composerOutlinedFieldSx,
  composerPanelBorderSx,
} from "./composerBorderStyles";
import { earliestSeriesEndDate } from "../../domain/events/eventRecurrence";
import {
  EVENT_DATE_PICKER_FORMAT,
  EVENT_DATE_PICKER_LOCALE,
  formatEventDisplayDate,
} from "../../utils/eventDateDisplay";

const REPEAT_TITLE_TOOLTIP =
  "This schedules recurring events and adds them to the calendar";

const frequencyLabels: Record<string, string> = {
  daily: "daily",
  weekly: "weekly",
  biweekly: "every 2 weeks",
  monthly: "monthly",
};

function buildRecurrencePreview(
  frequency: string,
  untilDate: Date | null,
): string {
  if (!frequency || frequency === "none") return "";
  const freq = frequencyLabels[frequency] || frequency;
  if (untilDate) {
    const formatted = formatEventDisplayDate(untilDate);
    return `This event repeats ${freq} until ${formatted}.`;
  }
  return `This event repeats ${freq}.`;
}

const EventRecurrenceSettingCard: React.FC<StepProps> = ({
  formData,
  errors,
  handleChange,
}) => {
  const theme = useTheme();
  const eventStartDate = useMemo(() => {
    if (formData.eventStartDate instanceof Date) return formData.eventStartDate;
    if (!formData.eventStartDate) return null;
    const parsed = new Date(formData.eventStartDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [formData.eventStartDate]);

  const frequency = formData.eventRecurrenceFrequency || "none";
  const untilDate = formData.eventRecurrenceUntil || null;
  const hasRecurrence = frequency !== "none";

  const earliestEnd = useMemo(
    () => (hasRecurrence ? earliestSeriesEndDate(eventStartDate, frequency) : null),
    [eventStartDate, frequency, hasRecurrence],
  );

  const recurrencePreview = useMemo(
    () => buildRecurrencePreview(frequency, untilDate),
    [frequency, untilDate],
  );

  const collapsedSummary = useMemo(() => {
    if (!hasRecurrence) return "Does not repeat";
    return recurrencePreview || "Repeating on a schedule";
  }, [hasRecurrence, recurrencePreview]);

  const [expanded, setExpanded] = useState(
    hasRecurrence || Boolean(errors.eventRecurrenceFrequency),
  );

  useEffect(() => {
    if (hasRecurrence || errors.eventRecurrenceFrequency) {
      setExpanded(true);
    }
  }, [errors.eventRecurrenceFrequency, hasRecurrence]);

  return (
    <Box
      sx={{
        ...composerPanelBorderSx,
        overflow: "hidden",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setExpanded((open) => !open);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          cursor: "pointer",
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
          }}
        >
          <RepeatOutlinedIcon fontSize="small" />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={400} lineHeight={1.3}>
              Repeat
            </Typography>
            <Tooltip title={REPEAT_TITLE_TOOLTIP} arrow>
              <IconButton
                size="small"
                aria-label={REPEAT_TITLE_TOOLTIP}
                sx={{
                  p: 0.25,
                  color: "text.secondary",
                  "&:hover": { color: "primary.main" },
                }}
                onClick={(event) => event.stopPropagation()}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.25, lineHeight: 1.35 }}
          >
            {expanded
              ? "Choose whether this event happens once or on a schedule."
              : collapsedSummary}
          </Typography>
        </Box>

        <ExpandMoreIcon
          sx={{
            flexShrink: 0,
            color: "text.secondary",
            transition: "transform 0.2s ease",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      <Collapse in={expanded}>
        <Box
          sx={{
            px: 2,
            pb: 2,
            pt: 0,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Box
            sx={{
              pt: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <FormControl
              fullWidth
              size="small"
              error={!!errors.eventRecurrenceFrequency && !hasRecurrence}
              sx={composerOutlinedFieldSx}
            >
              <InputLabel id="event-recurrence-frequency-label">
                Repeats
              </InputLabel>
              <Select
                labelId="event-recurrence-frequency-label"
                label="Repeats"
                value={frequency}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "eventRecurrenceFrequency",
                      value: e.target.value,
                    },
                  })
                }
              >
                <MenuItem value="none">Does not repeat</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="biweekly">Every 2 weeks</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
              {errors.eventRecurrenceFrequency && !hasRecurrence ? (
                <FormHelperText>
                  {errors.eventRecurrenceFrequency}
                </FormHelperText>
              ) : null}
            </FormControl>

            {hasRecurrence ? (
              <>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={EVENT_DATE_PICKER_LOCALE}
                >
                  <DatePicker
                    label="End date"
                    format={EVENT_DATE_PICKER_FORMAT}
                    value={untilDate}
                    minDate={earliestEnd ?? eventStartDate ?? undefined}
                    onChange={(date) =>
                      handleChange({
                        target: { name: "eventRecurrenceUntil", value: date },
                      })
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        error: !!errors.eventRecurrenceFrequency,
                        helperText: errors.eventRecurrenceFrequency,
                        sx: composerOutlinedFieldSx,
                      },
                    }}
                  />
                </LocalizationProvider>

                {recurrencePreview ? (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      border: "1px solid",
                      borderColor: alpha(theme.palette.primary.main, 0.12),
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.45 }}
                    >
                      {recurrencePreview}
                    </Typography>
                  </Box>
                ) : null}
              </>
            ) : null}
          </Box>
        </Box>
      </Collapse>

      {errors.eventRecurrenceFrequency && !expanded ? (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: "block", px: 2, pb: 1.25 }}
        >
          {errors.eventRecurrenceFrequency}
        </Typography>
      ) : null}
    </Box>
  );
};

export default EventRecurrenceSettingCard;
